"use client"

import {
  BarChart3,
  BookOpenCheck,
  Cable,
  FileClock,
  GitPullRequest,
  Home,
  Inbox,
  Search,
  Settings,
  ShieldCheck,
} from "lucide-react"
import { useRouter } from "next/navigation"
import { useEffect, useRef, useState } from "react"

const commands = [
  { label: "Overview", href: "/app", icon: Home, keywords: "dashboard home" },
  { label: "Cases", href: "/app/cases", icon: Inbox, keywords: "queue investigations" },
  { label: "Approvals", href: "/app/approvals", icon: ShieldCheck, keywords: "actions decisions" },
  { label: "Playbooks", href: "/app/playbooks", icon: BookOpenCheck, keywords: "workflow builder" },
  { label: "Connectors", href: "/app/connectors", icon: Cable, keywords: "integrations health" },
  {
    label: "Code proposals",
    href: "/app/code-proposals",
    icon: GitPullRequest,
    keywords: "pull requests",
  },
  { label: "Analytics", href: "/app/analytics", icon: BarChart3, keywords: "roi metrics" },
  { label: "Audit", href: "/app/audit", icon: FileClock, keywords: "history events" },
  { label: "Settings", href: "/app/settings", icon: Settings, keywords: "team roles policy" },
]

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()
  const [query, setQuery] = useState("")

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) {
      dialog.showModal()
      window.setTimeout(() => inputRef.current?.focus(), 0)
    }
    if (!open && dialog.open) dialog.close()
  }, [open])

  const filtered = commands.filter((command) =>
    `${command.label} ${command.keywords}`.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
  )

  return (
    <dialog
      ref={dialogRef}
      className="command-dialog"
      aria-labelledby="command-title"
      onClose={() => {
        setQuery("")
        onOpenChange(false)
      }}
      onCancel={(event) => {
        event.preventDefault()
        dialogRef.current?.close()
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current) dialogRef.current?.close()
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") dialogRef.current?.close()
      }}
    >
      <div className="command-panel">
        <h2 id="command-title" className="sr-only">
          Navigate Resolto
        </h2>
        <label className="command-search">
          <Search aria-hidden="true" />
          <span className="sr-only">Search destinations</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search cases, playbooks, settings…"
          />
          <kbd>Esc</kbd>
        </label>
        <div className="command-results">
          <span>Navigate</span>
          {filtered.length > 0 ? (
            filtered.map((command) => {
              const Icon = command.icon
              return (
                <button
                  key={command.href}
                  type="button"
                  onClick={() => {
                    dialogRef.current?.close()
                    router.push(command.href)
                  }}
                >
                  <Icon aria-hidden="true" />
                  <span>{command.label}</span>
                  <kbd>↵</kbd>
                </button>
              )
            })
          ) : (
            <p className="command-empty">No matching destination.</p>
          )}
        </div>
      </div>
    </dialog>
  )
}
